import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { images } from "../assets/images";

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "战争的伤痕", href: "#home" },
    { name: "白鸽计划", href: "#project" },
    { name: "善行足迹", href: "#solution" },
    { name: "成为守望者", href: "#action" },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-lg shadow-lg"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.a
            href="#home"
            className="flex items-center gap-3 group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {/* Logo图片 - 始终显示，添加白色背景确保可见 */}
            <div className="rounded-lg p-2 transition-all duration-300">
              <img 
                src={images.logo} 
                alt="CLÉMENT Logo" 
                className="h-8 w-auto"
              />
            </div>
            
            {/* 文字部分 */}
            <div className="flex flex-col">
              <span
                className={`transition-colors duration-300 ${
                  isScrolled ? "text-zinc-900" : "text-white"
                }`}
              >
                克来姆公益咖啡与茶
              </span>
              <span
                className={`text-xs tracking-widest transition-colors duration-300 ${
                  isScrolled ? "text-zinc-600" : "text-white/80"
                }`}
              >
                Clément Coffee & Tea Philanthropy
              </span>
            </div>
          </motion.a>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item, index) => (
              <motion.a
                key={item.name}
                href={item.href}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`text-sm transition-colors duration-300 hover:text-amber-600 ${
                  isScrolled ? "text-zinc-700" : "text-white"
                }`}
                onClick={(e) => handleNavClick(e, item.href)}
              >
                {item.name}
              </motion.a>
            ))}
          </div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Button
              onClick={(e) => handleNavClick(e as any, "#action")}
              className={`rounded-full transition-all duration-300 ${
                isScrolled
                  ? "bg-amber-600 hover:bg-amber-700 text-white"
                  : "bg-white hover:bg-white/90 text-amber-700"
              }`}
            >
              即刻点亮
            </Button>
          </motion.div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 transition-colors duration-300 ${
              isScrolled ? "text-zinc-900" : "text-white"
            }`}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mt-4 pb-4"
          >
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`text-sm transition-colors duration-300 hover:text-amber-600 ${
                    isScrolled ? "text-zinc-700" : "text-white"
                  }`}
                >
                  {item.name}
                </a>
              ))}
              <Button
                onClick={(e) => handleNavClick(e as any, "#join-us")}
                className={`rounded-full transition-all duration-300 ${
                  isScrolled
                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                    : "bg-white hover:bg-white/90 text-amber-700"
                }`}
              >
                即刻点亮
              </Button>
            </div>
          </motion.div>
        )}
      </nav>
    </motion.header>
  );
}

